import { Trash2, AlertTriangle } from "lucide-react";
import {
  PageHeading, Section, Steps, Callout,
  Diagram, MockTable, MockButton,
} from "../shared";

export default function SoftDeleteSection() {
  return (
    <div data-testid="guide-soft-delete">
      <PageHeading
        icon={Trash2}
        title="Soft Deletion & Restore"
        description="When an item is deleted, it isn't really destroyed — it's just hidden away. It disappears from the normal screens but can be brought back from the Deleted Items tab by an IT Admin or above."
      />

      <Callout variant="info" title="In plain words">
        Deleting here is like moving a file to the recycle bin, not shredding it. The item vanishes from everyday views, but the data stays safely in the background and can be restored if it was removed by mistake.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Delete an item when it was created in error or is genuinely no longer wanted. If you only need to stop work on something, cancelling it is usually better — deletion hides it from everyone.
        </p>
      </Section>

      <Section title="How it works">
        <Diagram
          title="Deleting, and restoring from the bin"
          frameLabel="Admin → Deleted Items"
          legend={[
            <>On an item's detail page, an admin clicks the red <span className="text-white">Delete</span> button and confirms.</>,
            <>The item then appears in <span className="text-white">Admin → Deleted Items</span> (IT Admin or above only).</>,
            <>Click the green <span className="text-white">Restore</span> button to bring it back to its previous status.</>,
          ]}
        >
          <MockButton label="Delete" marker={1} variant="danger" />
          <MockTable
            columns={["Deleted item", "Deleted on", "Action"]}
            rows={[
              ["Q3 portfolio pack", "14 May 2026", <MockButton label="Restore" marker={3} variant="ghost" />],
              ["IC minutes follow-up", "12 May 2026", <MockButton label="Restore" variant="ghost" />],
            ]}
          />
          <p className="text-2xs text-white/45 flex items-center gap-1.5">
            <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>
            All hidden items are listed here for IT Admins and above.
          </p>
        </Diagram>
        <Steps
          steps={[
            <>On an action item's detail page, an admin clicks the red <span className="text-red-300">Delete</span> button. A confirmation pops up.</>,
            <>The item is hidden — it disappears from Summary, Updates, Origins, and the MD Meetings queue. The data stays in the background with a deleted timestamp.</>,
            <>An IT Admin or above sees all hidden items under <span className="text-white">Admin → Deleted Items</span>.</>,
            <>From there, the green <span className="text-emerald-300">Restore</span> button brings the item back to its previous status.</>,
          ]}
        />
        <div className="flex items-start gap-2 mt-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-white/55"><span className="text-white font-medium">It's all recorded:</span> both the deletion and any later restore appear in the item's history, with who did it and when.</p>
        </div>
      </Section>

      <Callout variant="warning" title="There's no permanent delete in the app">
        The application never truly erases an item. If something must be permanently removed (for example, a data subject request), an IT Admin has to do it directly in the database — it can't be done through the screens.
      </Callout>
    </div>
  );
}
